import { DecimalPipe } from '@angular/common';
import { ElementRef, Injectable, inject } from '@angular/core';
import {
  SeatsBlockGroupResponseDto,
  SalesPlanBlockResponseDto,
  SalesPlanBlockFilterResponseDto,
  SubscriptionTopViewBackgroundResponseDto,
  FileResponse,
} from '@core/services/apiclient';
import * as fromSalesPlan from '@root-store/sales-plan-store/index';
import { select as ngrxSelect, Store } from '@ngrx/store';
import { select, selectAll, zoom, Selection, BaseType, ZoomBehavior } from 'd3';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';
import { Vector2d } from 'konva/lib/types';
import { PlanDetailsTooltip } from '../models/sales-plan.model';
import { TopviewMaxScaleValue, SearchResultIcon } from '../models/sales-plan-constants';
import { PlanDrawingService } from './plan-drawing.service';
import { FullResubscriptionSeatDto, SalesEventTypeEnum } from '@sales/models/sales.model';
import { TooltipStatusService } from '@core/services/tooltip-status.service';

@Injectable({
  providedIn: 'root',
})
export class SalesPlanTopviewService {
  private readonly store = inject<Store<fromSalesPlan.State>>(Store);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly planDrawingService = inject(PlanDrawingService);
  private readonly tooltipStatusService = inject(TooltipStatusService);

  public blockData: SalesPlanBlockResponseDto[];
  public zoomBehavior: ZoomBehavior<SVGSVGElement, unknown>;
  public svg: Selection<SVGSVGElement, unknown, null, undefined>;
  public blockPathTexts: {
    paths: Selection<BaseType, unknown, null, undefined>[];
    texts: Selection<BaseType, unknown, null, undefined>[];
  };
  public selectedBlockPath: Selection<BaseType, unknown, null, undefined>;
  public container: Selection<BaseType, unknown, null, undefined>;
  public currentScale$ = new BehaviorSubject<number>(1);
  public blockDetailsRequested$ = new Subject<PlanDetailsTooltip>();
  private destroy$: Subject<void> = new Subject();
  lastPointerPosition = { x: 0, y: 0 };
  previousBlockId = 0;

  public process(
    background: SubscriptionTopViewBackgroundResponseDto | FileResponse,
    data: SalesPlanBlockResponseDto[],
    svgWrapper: ElementRef,
    eventType: SalesEventTypeEnum,
    isResubPlan: boolean,
  ) {
    this.blockData = data || [];
    const image = new Image();
    if (eventType === SalesEventTypeEnum.Subscription) {
      if (background && 'picture' in background) {
        const extension = background.extension === 'UNKNOWN' ? 'png' : background.extension;
        image.src = `data:image/${extension};base64,${background.picture}`;
      }
    } else if (background && 'data' in background) {
      const url = URL.createObjectURL(background.data);
      image.src = url;
    }

    image.onload = (event: Event) => {
      this.createSVG(svgWrapper, image.naturalWidth, image.naturalHeight);
      this.createSVGContainer();
      this.appendImageToSVG(image?.src, { x: 0, y: 0 }, 'background-img');
      this.handleZoomAndPan(this.svg);
      this.drawBlocks(this.blockData);
      this.getBlockSearchResult(isResubPlan);
      if (isResubPlan) {
        this.searchResubBeneficiarySeatBlock();
      }
    };

    image.onerror = () => {};
    this.planDrawingService.updatePlanDrawingStatus(true);
  }

  /**
   * appends the top view image
   * @param container global container inside the svg
   * @param position x & y
   * @param id element identifier
   * @param className css class to help us select all images
   * @param imageSrc image element src
   */
  private appendImageToSVG(imageSrc: string, position: Vector2d, id: string, className?: string) {
    return this.container
      .append('svg:image')
      .attr('id', id)
      .attr('class', className)
      .attr('x', position?.x)
      .attr('y', position?.y)
      .attr('xlink:href', imageSrc);
  }

  /**
   * creates the svg element in which we render the plan view
   * @param wrapper div to which we append the svg
   * @param planWidth natural width of the image
   * @param planHeight natural height of the image
   * @returns svg
   */
  public createSVG(wrapper: ElementRef, planWidth: number, planHeight: number) {
    // remove the previous plan if exists;
    this.destroySvg();
    this.svg = select(wrapper.nativeElement)
      .append('svg')
      .attr('class', 'plan-svg')
      .attr('viewBox', `0 0 ${planWidth} ${planHeight}`)
      .attr('width', wrapper.nativeElement.offsetWidth)
      .attr('height', wrapper.nativeElement.offsetHeight)
      .attr('preserveAspectRatio', 'none');
  }

  public createSVGContainer() {
    this.container = this.svg.append('svg:g').attr('id', `plan-container`);
  }

  /**
   * draw blocks by their coordinates
   * @param blocks block data
   */

  private drawBlocks(blocks: SalesPlanBlockResponseDto[]) {
    this.selectedBlockPath = null;
    const blockPathsText = (blocks || []).map((block) => {
      const path = this.container
        .append('svg:path')
        .attr('id', `${block?.id}`)
        .attr(
          'class',
          `plan-blocks${this.isBlockUnnumbered(block) ? ' unnumbered' : ' '}${
            block?.availableSeats ? ' ' : ' disabled'
          }`,
        )
        .attr('d', `M ${block?.blockShape.coordinates} Z`)
        .attr('fill', block?.availableSeats ? block?.color : 'var(--color-grey-500)');
      if (this.isBlockUnnumbered(block)) {
        path.attr('block-type', 'unnumbered');
      }
      const blockText = this.appendTextToBlock(block);
      return { path, blockText };
    });
    const updatedPaths = blockPathsText.map((blockPathText) => blockPathText.path);
    const updatedTexts = blockPathsText.map((blockPathText) => blockPathText.blockText);
    this.blockPathTexts = { paths: updatedPaths, texts: updatedTexts };
    this.addEventToBlocks();
    this.getSelectedBlock();
    this.svg.node().addEventListener('click', () => {
      this.tooltipStatusService.setTooltipOpen(false);
    });
  }

  private addEventToBlocks() {
    let hoverTimer;
    this.blockPathTexts.paths.forEach((path, index, self) => {
      const otherPaths = self.filter((p) => p?.attr('id') !== path.attr('id'));
      path
        .on('click', (event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          this.selectBlock(path);
          this.tooltipStatusService.setTooltipOpen(false);
        })
        .on('mouseover', (event: MouseEvent) => {
          if (
            this.previousBlockId == parseInt(path.attr('id')) &&
            this.tooltipStatusService.getTooltipStatus()
          ) {
            return;
          }
          this.previousBlockId = parseInt(path.attr('id'));
          hoverTimer = setTimeout(() => {
            path.classed('active', true).classed('inactive', false);
            otherPaths.forEach((p) => p.classed('inactive', true));
            this.getBlockDetails(event, path);
          }, 1000);
        })
        .on('mouseout', () => {
          clearTimeout(hoverTimer);
          path.classed('active', false);
          if (self.every((p) => !p.classed('selected'))) {
            otherPaths.forEach((p) => p.classed('inactive', false));
          } else {
            path.classed('inactive', true);
          }
        });
    });
  }

  /**
   * draws the text inside the block
   * @param block block data
   */

  private appendTextToBlock(block: SalesPlanBlockResponseDto) {
    return this.container
      .append('svg:text')
      .attr('id', `block-text-${block?.id}`)
      .attr('class', 'block-available-places')
      .attr('x', block?.blockShape?.positionX)
      .attr('dx', block?.availableSeats ? 35 : 45)
      .attr('y', block?.blockShape?.positionY)
      .attr('font-family', 'Ubuntu')
      .attr('font-weight', 500)
      .attr('font-size', '0.625rem')
      .attr('fill', `var(${block?.availableSeats ? '--color-grey-700' : '--color-white'})`)
      .text(block?.availableSeats ? this.decimalPipe.transform(block?.availableSeats, '1.0-2') : '/');
  }

  private searchResubBeneficiarySeatBlock() {
    this.store
      .pipe(ngrxSelect(fromSalesPlan.selectors.selectResubBeneficiarySeat), takeUntil(this.destroy$))
      .subscribe({
        next: (result: FullResubscriptionSeatDto) => {
          if (result?.isAvailable) {
            this.renderSearchResultIcon(result.blockId);
          }
        },
      });
  }

  private getBlockSearchResult(isResubPlan: boolean) {
    this.store
      .pipe(ngrxSelect(fromSalesPlan.selectors.selectSeatSearchResult), takeUntil(this.destroy$))
      .subscribe({
        next: (result: SeatsBlockGroupResponseDto[]) => {
          if (!isResubPlan) {
            this.resetZoneSearchResult();
          }
          (result || [])
            .map(({ blockId }) => blockId)
            .filter((blockId) => !!blockId)
            .forEach((blockId) => this.renderSearchResultIcon(blockId));
        },
      });
  }

  private renderSearchResultIcon(blockId: number): void {
    try {
      const blockText = select(`#block-text-${blockId}`);
      if (!blockText) return;

      const image: any = this.appendImageToSVG(
        SearchResultIcon,
        {
          x: parseInt(blockText.attr('x')),
          y: parseInt(blockText.attr('y')),
        },
        `${blockId}-img`,
        'svg-search-result',
      );

      image
        .attr('width', 30)
        .attr('height', 40)
        .attr('transform', `translate(${image.attr('width')}, -${+image.attr('height') + 4})`);
    } catch (error) {
      return null;
    }
  }

  /**
   * returns the observable that contains data for zone details tooltip
   */
  public requestBlockDetails() {
    return this.blockDetailsRequested$.asObservable();
  }

  private resetZoneSearchResult() {
    selectAll('.svg-search-result').remove();
  }

  private isBlockUnnumbered(block) {
    return block?.type === 'UNNUMBERED';
  }

  /**
   * adds event listeners to svg: zoom & pan
   * @param svg svg element
   */
  private handleZoomAndPan(element: any) {
    const node = element.node();
    // if svg then get width and height from bbox
    const { width, height } = node.getBBox ? node.getBBox() : node;
    this.zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, TopviewMaxScaleValue])
      .extent([
        [0, 0],
        [width, height],
      ])
      .translateExtent([
        [0, 0],
        [width, height],
      ]);

    this.zoomBehavior
      .filter((event: PointerEvent) => {
        // zoom event prevents click handling: https://github.com/d3/d3/issues/2887
        return event instanceof WheelEvent || (event instanceof MouseEvent && event.button === 2);
      })
      .on('zoom', (zoomEvent) => {
        this.container.attr('transform', zoomEvent.transform);
        this.currentScale$.next(zoomEvent?.transform?.k);
        this.updateZoomExtents(zoomEvent?.transform?.k);
      });
    this.svg.style('transform-origin', 'center');
    element.call(this.zoomBehavior);
  }

  /**
   * each time the zoom/pan action is executed, we need to refresh the translate extent with
   * new limits
   * @param scale current scale ratio
   */
  private updateZoomExtents(scale: number) {
    const { width, height } = this.svg.node().getBBox();
    this.zoomBehavior.translateExtent([
      [0, 0],
      [width / scale, height / scale],
    ]);
  }

  private selectBlock(selectedPath: Selection<BaseType, unknown, null, undefined>) {
    const id = parseInt(selectedPath.attr('id'));
    if (selectedPath.attr('block-type') === 'unnumbered') {
      this.store.dispatch(
        fromSalesPlan.actions.setUnnumberedBlockId({
          unnumberedBlockId: id,
        }),
      );
    } else {
      this.store.dispatch(fromSalesPlan.actions.loadPlanData({ blockId: id }));
    }
  }

  public selectNextBlock() {
    const nextBlockIndex = this.getNextBlockIndex();
    if (nextBlockIndex !== -1) {
      this.selectBlock(this.blockPathTexts.paths[nextBlockIndex]);
    }
  }

  public selectPreviousBlock() {
    const previousBlockIndex = this.getPreviousBlockIndex();
    if (previousBlockIndex !== -1) {
      this.selectBlock(this.blockPathTexts.paths[previousBlockIndex]);
    }
  }

  public getNextBlockData() {
    const nextBlockIndex = this.getNextBlockIndex();
    if (nextBlockIndex !== -1) {
      return this.blockData[nextBlockIndex];
    }
    return null;
  }

  public getPreviousBlockData() {
    const previousBlockIndex = this.getPreviousBlockIndex();
    if (previousBlockIndex !== -1) {
      return this.blockData[previousBlockIndex];
    }
    return null;
  }

  private getNextBlockIndex(currentIndex?: number) {
    const currentBlockIndex = !isNaN(currentIndex)
      ? currentIndex
      : (this.blockPathTexts.paths || []).indexOf(this.selectedBlockPath);
    if (currentBlockIndex === -1) {
      return -1;
    }
    const nextBlockIndex =
      currentBlockIndex + 1 < this.blockPathTexts.paths?.length ? currentBlockIndex + 1 : 0;
    // if unnumbered or disabled then skip
    if (
      this.blockPathTexts.paths?.[nextBlockIndex]?.classed('unnumbered') ||
      this.blockPathTexts.paths?.[nextBlockIndex]?.classed('disabled')
    ) {
      return this.getNextBlockIndex(nextBlockIndex);
    }
    return nextBlockIndex;
  }

  private getPreviousBlockIndex(currentIndex?: number) {
    const currentZoneIndex = !isNaN(currentIndex)
      ? currentIndex
      : (this.blockPathTexts.paths || []).indexOf(this.selectedBlockPath);
    if (currentZoneIndex === -1) {
      return -1;
    }
    const previousZoneIndex =
      currentZoneIndex - 1 >= 0 ? currentZoneIndex - 1 : this.blockPathTexts.paths?.length - 1;
    // if unnumbered or disabled then skip
    if (
      this.blockPathTexts.paths?.[previousZoneIndex]?.classed('unnumbered') ||
      this.blockPathTexts.paths?.[previousZoneIndex]?.classed('disabled')
    ) {
      return this.getPreviousBlockIndex(previousZoneIndex);
    }
    return previousZoneIndex;
  }

  private getSelectedBlock() {
    this.store
      .pipe(
        ngrxSelect(fromSalesPlan.selectors.selectBlockId),
        takeUntil(this.destroy$),
        filter((blockId) => !!blockId),
      )
      .subscribe({
        next: (blockId: number) => {
          this.selectedBlockPath = (this.blockPathTexts.paths || []).find(
            (p) => p?.attr('id') === `${blockId}`,
          );
          this.selectedBlockPath.classed('selected', !this.selectedBlockPath.classed('unnumbered'));
          (this.blockPathTexts.paths || [])
            .filter((p) => p?.attr('id') !== this.selectedBlockPath.attr('id'))
            .forEach((p) => p.classed('inactive', true).classed('selected', false));
        },
      });
  }

  /**
   * sends data to the component in order to get the block details
   */
  private getBlockDetails(event: MouseEvent, path: Selection<BaseType, unknown, null, undefined>) {
    const svgRect = this.svg.node().getBoundingClientRect();
    const currentPointerPosition = {
      x: event.pageX - svgRect.x,
      // 100 is the padding to avoid getting a hidden tooltip in the minimap
      y: event.pageY - (this.svg.classed('minimap') ? 100 : svgRect.y),
    };

    if (
      currentPointerPosition.x !== this.lastPointerPosition.x ||
      currentPointerPosition.y !== this.lastPointerPosition.y
    ) {
      this.blockDetailsRequested$.next({
        id: parseInt(path.attr('id')),
        pointerPosition: currentPointerPosition,
        isUnnumbered: path.attr('block-type') === 'unnumbered',
      });
      this.lastPointerPosition = currentPointerPosition;
    }
  }

  public refreshBlockAvailabilities(filteredBlocks: SalesPlanBlockFilterResponseDto[]) {
    this.blockData = this.blockData.map((blockData) => {
      const filteredBlock = filteredBlocks.find(
        (currentFilteredBlock) => currentFilteredBlock.id == blockData.id,
      );
      return filteredBlock
        ? { ...blockData, availableSeats: filteredBlock.availableSeats }
        : { ...blockData, availableSeats: 0 };
    });
    this.updateBlocks();
  }
  private updateBlocks() {
    this.selectedBlockPath = null;
    this.updatePaths();
    this.updateTexts();
  }
  private updatePaths() {
    this.blockPathTexts.paths.forEach((svgPath) => {
      const blockData = this.blockData.find((block) => block.id.toString() == svgPath.attr('id'));
      svgPath.attr('class', `plan-blocks }${blockData.availableSeats ? ' ' : ' disabled'}`);
      svgPath.attr('fill', blockData.availableSeats ? blockData.color : 'var(--color-grey-500)');
    });
  }

  private updateTexts() {
    this.blockPathTexts.texts.forEach((svgText) => {
      const blockData = this.blockData.find((block) => `block-text-${block.id}` == svgText.attr('id'));
      svgText.attr('dx', blockData.availableSeats ? 35 : 45);
      svgText.attr('fill', `var(${blockData.availableSeats ? '--color-grey-700' : '--color-white'})`);
      svgText.text(
        blockData.availableSeats ? this.decimalPipe.transform(blockData.availableSeats, '1.0-2') : '/',
      );
    });
  }

  public destroySvg() {
    this.currentScale$.next(1);
    this.destroy$.next();
    if (this.svg) {
      this.svg.remove();
    }
    this.lastPointerPosition = { x: 0, y: 0 };
    this.previousBlockId = 0;
  }
}
